import React, { useState, useEffect } from 'react';
import SignalCard from './SignalCard';

interface Signal {
  id: number;
  pair: string;
  type: string;
  direction: 'up' | 'down';
  entryTime: string;
  duration: string;
  forecast: number;
  payout: number;
}

const CURRENCY_PAIRS = ['GBPJPY', 'EURGBP', 'EURSGD', 'AUDUSD', 'USDCAD', 'NZDCHF'];
const DURATIONS = ['M1', 'M2', 'M5'];

const generateRandomSignal = (id: number): Signal => {
  const pair = CURRENCY_PAIRS[Math.floor(Math.random() * CURRENCY_PAIRS.length)];
  const direction = Math.random() > 0.5 ? 'up' : 'down';
  const forecast = parseFloat((Math.random() * (99 - 95) + 95).toFixed(1));
  const payout = parseFloat((Math.random() * (93 - 88) + 88).toFixed(1));
  const duration = DURATIONS[Math.floor(Math.random() * DURATIONS.length)];
  const now = new Date();
  now.setSeconds(now.getSeconds() - Math.floor(Math.random() * 120));
  const entryTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return { id, pair, type: 'OTC', direction, entryTime, duration, forecast, payout };
};

// ---------------- Components ----------------
const StatusBar = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="absolute top-0 left-0 right-0 h-10 px-4 flex justify-between items-center text-white text-xs z-10">
      <span>{time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-lg flex justify-center items-center gap-4">
        <div className="w-12 h-1 bg-gray-700 rounded-full"></div>
        <div className="w-2 h-2 bg-gray-700 rounded-full"></div>
      </div>
      <div className="flex items-center gap-1.5">
        <i className="bx bx-wifi"></i>
        <i className="bx bxs-battery"></i>
      </div>
    </div>
  );
};

const BottomNav = () => (
  <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/40 to-transparent flex items-end pb-2 px-2">
    <div className="flex justify-around items-center w-full h-14 bg-[#1e2c2b]/80 backdrop-blur-md rounded-full border border-gray-600/50">
      <i className="bx bxs-dashboard text-2xl text-gray-400"></i>
      <i className="bx bxs-chat text-2xl text-gray-400"></i>
      <div className="bg-yellow-400/80 text-black rounded-full px-5 py-2 flex items-center gap-2 shadow-lg shadow-yellow-500/20">
        <i className="bx bxs-zap text-xl"></i>
        <span className="text-sm font-bold">توصيات</span>
      </div>
      <i className="bx bxs-cog text-2xl text-gray-400"></i>
      <i className="bx bxs-user-circle text-2xl text-gray-400"></i>
    </div>
  </div>
);

const LoadingOverlay = () => (
  <div className="absolute inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center text-center p-4 z-20 overflow-hidden">
    <div className="relative w-28 h-28 flex items-center justify-center">
      <i className="bx bxs-bot text-7xl text-cyan-400 opacity-20"></i>
      <div className="absolute inset-0 rounded-full border-2 border-cyan-400/30"></div>
      <div className="absolute inset-2 rounded-full border-2 border-cyan-400/30 animate-spin"></div>
      <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-cyan-300/50 to-transparent animate-scanline"></div>
    </div>
    <p className="text-white font-semibold mt-6 text-sm">
      يتم الآن تحليل السوق واختيار أماكن صفقات مناسبة...
    </p>
  </div>
);

// ---------------- Main ----------------
const PhoneScreen: React.FC<{ isLoading: boolean; analysisKey: number }> = ({ isLoading, analysisKey }) => {
  const [signals, setSignals] = useState<Signal[]>([]);

  useEffect(() => {
    if (analysisKey > 0) {
      setSignals(Array.from({ length: 3 }, (_, i) => generateRandomSignal(i)));
    }
  }, [analysisKey]);

  return (
    <div className="relative w-full h-full bg-[#060c1f] flex flex-col pt-10 rounded-[2rem] overflow-hidden">
      <StatusBar />
      {isLoading && <LoadingOverlay />}

      <header className="px-4 pb-2 shrink-0">
        <h1 className="text-xl font-bold text-center text-white">SIGNAL - التوصيات</h1>
      </header>

      <main className="flex-grow flex flex-col justify-evenly p-2 pb-20">
        {signals.length > 0 ? (
          signals.map((signal, index) => (
            <div
              key={signal.id}
              className="animate-[slideInUp_0.5s_ease-out_forwards]"
              style={{ opacity: 0, animationDelay: `${index * 150}ms` }}
            >
              <SignalCard signal={signal} />
            </div>
          ))
        ) : (
          !isLoading && (
            <div className="flex items-center justify-center h-full text-center text-gray-400/70 p-4 text-sm">
              <p>اضغط على زر "تحليل السوق" لبدء التحليل وارسال صفقات جديده (للتجربه فقط).</p>
            </div>
          )
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default PhoneScreen;
